// ==========================================================================
// screens/Calendar.tsx — the family calendar
// FF-020 part 1 (9 Oct 2026), redesigned in the FF-026 pass the same day,
// and again for FF-062 (10 Oct, James: "bars with words and quick hover
// details not dots … bookings over multiple days should be a long bar with
// the text scrolling through it … put the days activity above the calendar
// … make it a little more special").
//
//   Week   (desktop default): hour grid 6am–10pm, all-day strip on top,
//          events as coloured blocks. Drag to move; click empty space to add
//          at that time. A red line marks now.
//   Month  (phone default): bars with titles on the phone too. A booking over
//          several days is one long bar whose title scrolls if it doesn't
//          fit. Hover (desktop) or tap (phone) a bar for a details card.
//          On the phone the chosen day sits above the grid.
//   Agenda: the next 90 days.
// Drag and drop is desktop only (James: "phone would be annoying"). Moving a
// repeating event asks: just this one, or every repeat.
// ==========================================================================
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format, isSameMonth, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, MapPin, Pencil, Plus, Repeat, Sun } from "lucide-react";
import { Card, Button } from "@/components/ui";
import EventSheet from "@/components/EventSheet";
import CalendarHero from "@/components/calendar/CalendarHero";
import { usePeople, type Person } from "@/lib/household";
import { expand, isMultiDay, mapsLink, occTitle, spanOf, tokenColour, useCalendarExtras, useEvents, useMoveEvent, ymd, type Occurrence } from "@/lib/calendar";
import { useNavigate } from "react-router-dom";

const HOUR_PX = 52;
const START_H = 6;
const END_H = 22;
const TZ = "Australia/Brisbane";

type View = "week" | "month" | "agenda";

function useIsDesktop() {
  const q = "(min-width: 1024px)";
  const [is, setIs] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setIs(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return is;
}

/** Minutes since midnight, Brisbane time. */
const minsOf = (d: Date) => {
  const [h, m] = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ }).split(":").map(Number);
  return h * 60 + m;
};
const at = (date: string, mins: number) => new Date(`${date}T${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}:00+10:00`);
const clock = (d: Date) => d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: TZ }).replace(/\s/g, "").replace(":00", "").toLowerCase();

const colourOf = (o: Occurrence, people: Person[]) => o.event.colour ?? people.find((x) => x.id === o.people[0])?.colour ?? "text-muted";
const multiDay = isMultiDay;

/** "Sat 10 Oct · 9am – 10am", "Sat 10 – Tue 13 Oct · 4 days", … */
function whenText(o: Occurrence) {
  const { first, last } = spanOf(o);
  const f = parseISO(first), l = parseISO(last);
  const days = Math.round((l.getTime() - f.getTime()) / 86400000) + 1;
  if (!o.start) return last > first ? `${format(f, isSameMonth(f, l) ? "EEE d" : "EEE d MMM")} – ${format(l, "EEE d MMM")} · ${days} days` : `${format(f, "EEE d MMM")} · all day`;
  if (last > first) return `${format(f, "EEE d MMM")} ${clock(o.start)} – ${format(l, "EEE d MMM")}${o.end ? ` ${clock(o.end)}` : ""}`;
  return `${format(f, "EEE d MMM")} · ${clock(o.start)}${o.end ? ` – ${clock(o.end)}` : ""}`;
}

function whoLabel(o: Occurrence, people: Person[]) {
  const names = o.people.map((id) => people.find((p) => p.id === id)?.display_name).filter(Boolean);
  return names.length ? names.join(", ") : "Family";
}

// ---------------------------------------------------------------------------
// Details card: hover on desktop, tap on the phone
// ---------------------------------------------------------------------------
type PeekState = { o: Occurrence; rect: DOMRect; pinned: boolean } | null;
const PeekCtx = createContext<{ show: (o: Occurrence, el: HTMLElement, pinned: boolean) => void; hide: () => void }>({ show: () => {}, hide: () => {} });

function Peek({ peek, people, onEdit, onClose }: { peek: NonNullable<PeekState>; people: Person[]; onEdit: (o: Occurrence) => void; onClose: () => void }) {
  const { o, rect, pinned } = peek; const c = colourOf(o, people);
  const W = Math.min(300, window.innerWidth - 24);
  const left = Math.max(12, Math.min(window.innerWidth - W - 12, rect.left + rect.width / 2 - W / 2));
  const below = rect.bottom + 8 + 190 < window.innerHeight;
  const pos: React.CSSProperties = below ? { top: rect.bottom + 8 } : { bottom: window.innerHeight - rect.top + 8 };
  const card = (
    <div role="dialog" aria-label={occTitle(o)} className={`cal-pop fixed z-50 overflow-hidden rounded-xl bg-surface shadow-soft ring-1 ring-line ${pinned ? "" : "pointer-events-none"}`} style={{ ...pos, left, width: W }} onClick={(e) => e.stopPropagation()}>
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${tokenColour(c)}, ${tokenColour(c, 0.45)})` }} />
      <div className="space-y-1.5 p-4">
        <div className="text-[15px] font-semibold leading-snug">{occTitle(o)}</div>
        <div className="num text-sm text-ink-muted">{whenText(o)}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {o.event.virtual === "holiday" ? <span className="text-ink-muted">Public holiday</span> : <span className="flex items-center gap-1.5 font-medium" style={{ color: tokenColour(c) }}><span className="h-2 w-2 rounded-full" style={{ background: tokenColour(c) }} />{o.event.virtual === "birthday" ? "Birthday" : whoLabel(o, people)}</span>}
          {o.event.repeat !== "none" && !o.event.virtual && <span className="flex items-center gap-1 text-ink-muted"><Repeat className="h-3.5 w-3.5" />{o.event.repeat}</span>}
        </div>
        {o.event.location && (pinned
          ? <a href={mapsLink(o.event.location)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-primary"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{o.event.location}</span></a>
          : <div className="flex items-center gap-1 text-sm text-ink-muted"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{o.event.location}</span></div>)}
        {o.event.notes && !o.event.virtual && <p className="line-clamp-3 text-sm text-ink-muted">{o.event.notes}</p>}
        {pinned && o.event.virtual !== "holiday" && (
          <button type="button" onClick={() => { onClose(); onEdit(o); }} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-surface-raised py-2.5 text-sm font-semibold text-ink hover:bg-primary/10 hover:text-primary">
            <Pencil className="h-4 w-4" /> {o.event.virtual === "birthday" ? "Open contact" : "Edit"}
          </button>
        )}
      </div>
    </div>
  );
  return pinned ? <><div className="fixed inset-0 z-40" onClick={onClose} />{card}</> : card;
}

/** The title inside a bar. For a multi-day booking a title that doesn't fit scrolls slowly. */
function BarText({ text, scroll }: { text: string; scroll: boolean }) {
  const box = useRef<HTMLSpanElement>(null); const txt = useRef<HTMLSpanElement>(null);
  const [over, setOver] = useState(0);
  useLayoutEffect(() => {
    if (!scroll) return;
    const check = () => { const b = box.current, t = txt.current; if (b && t) setOver(t.offsetWidth > b.clientWidth + 1 ? t.offsetWidth : 0); };
    check();
    const ro = new ResizeObserver(check); if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, [scroll, text]);
  if (!scroll) return <span className="min-w-0 truncate">{text}</span>;
  return (
    <span ref={box} className="relative min-w-0 flex-1 overflow-hidden" style={over ? { maskImage: "linear-gradient(90deg, transparent, #000 8px, #000 calc(100% - 12px), transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8px, #000 calc(100% - 12px), transparent)" } : undefined}>
      <span className="cal-marquee" data-run={over ? "1" : "0"} style={{ ["--cal-marquee-s" as string]: `${Math.max(7, over / 22)}s` }}>
        <span ref={txt} className={over ? "pr-10" : ""}>{text}</span>
        {over > 0 && <span aria-hidden className="pr-10">{text}</span>}
      </span>
    </span>
  );
}

type Seg = { o: Occurrence; from: number; to: number; startsHere: boolean; endsHere: boolean; multi: boolean };
/** Bars for one week: a multi-day event is one bar across its days; the rest are one day each.
 *  Longest first, then stacked into lanes so nothing overlaps. */
function weekBars(keys: string[], byDay: Map<string, Occurrence[]>, keep: (o: Occurrence) => boolean) {
  const seen = new Map<string, Seg>();
  const lastKey = keys[keys.length - 1];
  for (const k of keys) {
    for (const o of byDay.get(k) ?? []) {
      if (!keep(o)) continue;
      const { first, last } = spanOf(o);
      const id = `${o.event.id}:${first}`;
      if (seen.has(id)) continue;
      const from = first < keys[0] ? 0 : keys.indexOf(first);
      const to = last > lastKey ? keys.length - 1 : keys.indexOf(last);
      seen.set(id, { o, from: Math.max(0, from), to: Math.max(from, to), startsHere: first >= keys[0], endsHere: last <= lastKey, multi: last > first });
    }
  }
  const segs = [...seen.values()].sort((a, b) => (b.to - b.from) - (a.to - a.from) || a.from - b.from || (a.o.start?.getTime() ?? 0) - (b.o.start?.getTime() ?? 0));
  const used: boolean[][] = []; const placed: { seg: Seg; lane: number }[] = [];
  for (const seg of segs) {
    let lane = 0;
    while (used[lane] && used[lane].slice(seg.from, seg.to + 1).some(Boolean)) lane++;
    used[lane] = used[lane] ?? Array(keys.length).fill(false);
    for (let c = seg.from; c <= seg.to; c++) used[lane][c] = true;
    placed.push({ seg, lane });
  }
  return { placed, laneCount: used.length };
}

/** One bar: the person's colour with a soft sheen, white text; square ends where
 *  it carries on into the next/previous week. Hover for details on desktop;
 *  tap for details on the phone. */
function Bar({ seg, people, desktop, onOpen, onDragStart, style, className = "", compact = false }: { seg: Seg; people: Person[]; desktop: boolean; onOpen: (o: Occurrence) => void; onDragStart: () => void; style: React.CSSProperties; className?: string; compact?: boolean }) {
  const { o } = seg; const canDrag = desktop && !o.event.virtual && seg.startsHere && !o.part;
  const peek = useContext(PeekCtx); const t = useRef<number>();
  const c = colourOf(o, people);
  const holiday = o.event.virtual === "holiday";
  return (
    <button type="button" draggable={canDrag}
      onDragStart={(e) => { e.stopPropagation(); peek.hide(); onDragStart(); }}
      onMouseEnter={(e) => { if (!desktop) return; const el = e.currentTarget; t.current = window.setTimeout(() => peek.show(o, el, false), 280); }}
      onMouseLeave={() => { window.clearTimeout(t.current); if (desktop) peek.hide(); }}
      onClick={(e) => { e.stopPropagation(); window.clearTimeout(t.current); if (desktop) { peek.hide(); onOpen(o); } else peek.show(o, e.currentTarget, true); }}
      aria-label={`${occTitle(o)}, ${whenText(o)}`}
      style={{
        ...style,
        background: holiday ? `repeating-linear-gradient(135deg, ${tokenColour(c, 0.16)} 0 6px, ${tokenColour(c, 0.08)} 6px 12px)` : `linear-gradient(180deg, ${tokenColour(c, 0.88)}, ${tokenColour(c)})`,
        color: holiday ? tokenColour(c) : "white",
        boxShadow: holiday ? `inset 0 0 0 1px ${tokenColour(c, 0.35)}` : `inset 0 1px 0 rgb(255 255 255 / 0.25), 0 1px 2px ${tokenColour(c, 0.35)}`,
      }}
      className={`relative z-[1] flex min-w-0 items-center gap-1 overflow-hidden text-left font-semibold transition hover:brightness-105 ${compact ? "mx-0.5 px-1 text-[10px] leading-none" : "mx-1 px-2 text-xs"} ${seg.startsHere ? "rounded-l-md" : "-ml-px rounded-l-none"} ${seg.endsHere ? "rounded-r-md" : "-mr-px rounded-r-none"} ${canDrag ? "cursor-grab" : ""} ${className}`}>
      {o.start && seg.startsHere && !compact && <span className="num shrink-0 font-normal opacity-80">{clock(o.start)}</span>}
      <BarText text={occTitle(o)} scroll={seg.multi} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Week view
// ---------------------------------------------------------------------------
function lanes(list: Occurrence[]) {
  const timed = list.filter((o) => o.start).sort((a, b) => a.start!.getTime() - b.start!.getTime());
  const ends: number[] = [];
  const lane = new Map<string, number>();
  for (const o of timed) {
    const s = minsOf(o.start!); const e = o.end ? minsOf(o.end) : s + 45;
    let i = ends.findIndex((x) => x <= s);
    if (i === -1) { i = ends.length; ends.push(e); } else ends[i] = e;
    lane.set(o.key, i);
  }
  return { lane, count: Math.max(1, ends.length) };
}

function WeekView({ weekStart, byDay, people, desktop, onOpen, onAdd, onMove }: {
  weekStart: Date; byDay: Map<string, Occurrence[]>; people: Person[]; desktop: boolean;
  onOpen: (o: Occurrence) => void; onAdd: (date: string, time?: string) => void;
  onMove: (o: Occurrence, date: string, start?: Date) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = ymd(new Date());
  const scroller = useRef<HTMLDivElement>(null);
  const drag = useRef<{ occ: Occurrence; grabMin: number } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  useEffect(() => { scroller.current?.scrollTo({ top: (7 - START_H) * HOUR_PX }); }, []);

  const yToMins = (e: { clientY: number }, col: HTMLElement) => {
    const y = e.clientY - col.getBoundingClientRect().top;
    return START_H * 60 + (y / HOUR_PX) * 60;
  };
  const snap = (m: number, to = 15) => Math.max(START_H * 60, Math.min(END_H * 60 - to, Math.round(m / to) * to));

  function dropTimed(e: DragEvent<HTMLDivElement>, date: string) {
    e.preventDefault(); setOver(null);
    const d = drag.current; drag.current = null;
    if (!d || !d.occ.start) return;
    const mins = snap(yToMins(e, e.currentTarget) - d.grabMin);
    onMove(d.occ, date, at(date, mins));
  }
  function dropAllDay(e: DragEvent<HTMLDivElement>, date: string) {
    e.preventDefault(); setOver(null);
    const d = drag.current; drag.current = null;
    if (!d || d.occ.start) return;
    onMove(d.occ, date);
  }

  return (
    <Card className="overflow-hidden p-0">
      {/* day headings + all-day strip */}
      <div className="grid border-b border-line" style={{ gridTemplateColumns: "56px repeat(7, minmax(0, 1fr))" }}>
        <div />
        {days.map((d) => {
          const key = ymd(d); const isToday = key === today;
          return (
            <div key={key} className="border-l border-line px-2 pb-2 pt-3 text-center">
              <div className={`text-xs ${isToday ? "font-semibold text-primary" : "text-ink-muted"}`}>{format(d, "EEE")}</div>
              <div className={`num mx-auto mt-0.5 flex h-9 w-9 items-center justify-center rounded-full text-lg font-semibold ${isToday ? "bg-primary text-white" : ""}`}>{format(d, "d")}</div>
            </div>
          );
        })}
        <div className="flex items-center justify-end pr-2 text-[10px] text-ink-muted" style={{ gridRow: 2 }}>all day</div>
        {(() => {
          const keys = days.map(ymd);
          const { placed, laneCount } = weekBars(keys, byDay, (o) => !o.start || multiDay(o));
          return (
            <div className="relative col-span-7 grid border-t border-line" style={{ gridColumn: "2 / span 7", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gridTemplateRows: `repeat(${Math.max(1, laneCount)}, 24px)`, rowGap: 4, paddingBlock: 5 }}>
              {keys.map((key, c) => (
                <div key={key} style={{ gridColumn: c + 1, gridRow: "1 / -1", marginBlock: -5 }} onDragOver={(e) => { if (drag.current && !drag.current.occ.start) { e.preventDefault(); setOver(`a:${key}`); } }} onDragLeave={() => setOver(null)} onDrop={(e) => dropAllDay(e, key)}
                  className={`min-h-[34px] border-l border-line ${over === `a:${key}` ? "bg-primary/10" : ""}`} />
              ))}
              {placed.map(({ seg, lane }) => (
                <Bar key={`${seg.o.key}:w`} seg={seg} people={people} desktop={desktop} onOpen={onOpen} onDragStart={() => { drag.current = { occ: seg.o, grabMin: 0 }; }}
                  style={{ gridColumn: `${seg.from + 1} / ${seg.to + 2}`, gridRow: lane + 1 }} className="flex" />
              ))}
            </div>
          );
        })()}
      </div>

      {/* hour grid */}
      <div ref={scroller} className="relative max-h-[640px] overflow-y-auto">
        <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, minmax(0, 1fr))" }}>
          <div className="relative" style={{ height: (END_H - START_H) * HOUR_PX }}>
            {Array.from({ length: END_H - START_H }, (_, i) => (
              <div key={i} className="num absolute right-2 text-[11px] text-ink-muted" style={{ top: i * HOUR_PX + 2 }}>{format(new Date(2026, 0, 1, START_H + i), "ha").toLowerCase()}</div>
            ))}
          </div>
          {days.map((d) => {
            const key = ymd(d);
            const list = (byDay.get(key) ?? []).filter((o) => o.start && !multiDay(o));
            const { lane, count } = lanes(list);
            const nowMins = minsOf(now);
            return (
              <div key={key}
                onDragOver={(e) => { if (drag.current?.occ.start) { e.preventDefault(); setOver(`t:${key}`); } }}
                onDragLeave={() => setOver(null)}
                onDrop={(e) => dropTimed(e, key)}
                onClick={(e) => { if (e.target === e.currentTarget) { const m = snap(yToMins(e, e.currentTarget), 30); onAdd(key, `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`); } }}
                className={`relative border-l border-line ${over === `t:${key}` ? "bg-primary/5" : ""} ${key === today ? "bg-primary/[0.03]" : ""}`}
                style={{ height: (END_H - START_H) * HOUR_PX, backgroundImage: `repeating-linear-gradient(to bottom, rgb(var(--border)) 0 1px, transparent 1px ${HOUR_PX}px)` }}>
                {key === today && nowMins > START_H * 60 && nowMins < END_H * 60 && (
                  <div className="pointer-events-none absolute inset-x-0 z-10 flex items-center" style={{ top: ((nowMins - START_H * 60) / 60) * HOUR_PX }}>
                    <span className="-ml-1 h-2 w-2 rounded-full bg-bad" /><span className="h-px flex-1 bg-bad" />
                  </div>
                )}
                {list.map((o) => {
                  const s = minsOf(o.start!); const e = o.end ? minsOf(o.end) : s + 45;
                  const top = ((Math.max(s, START_H * 60) - START_H * 60) / 60) * HOUR_PX;
                  const h = Math.max(22, ((e - Math.max(s, START_H * 60)) / 60) * HOUR_PX - 2);
                  const c = colourOf(o, people); const l = lane.get(o.key) ?? 0;
                  return (
                    <button key={o.key} type="button" draggable={desktop}
                      onDragStart={(ev) => { const r = (ev.currentTarget as HTMLElement).getBoundingClientRect(); drag.current = { occ: o, grabMin: ((ev.clientY - r.top) / HOUR_PX) * 60 }; ev.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => setOver(null)}
                      onClick={() => onOpen(o)}
                      className={`absolute flex flex-col items-start justify-start overflow-hidden rounded-md border-l-[3px] px-2 py-1 text-left shadow-sm transition hover:shadow-soft ${desktop ? "cursor-grab active:cursor-grabbing" : ""}`}
                      style={{ top, height: h, left: `calc(${(l / count) * 100}% + 2px)`, width: `calc(${100 / count}% - 4px)`, background: tokenColour(c, 0.14), borderColor: tokenColour(c) }}>
                      <span className="block truncate text-xs font-semibold text-ink">{occTitle(o)}</span>
                      {h > 34 && <span className="num block truncate text-[11px] text-ink-muted">{clock(o.start!)}{o.end ? `–${clock(o.end)}` : ""}</span>}
                      {h > 52 && o.event.location && <span className="block truncate text-[11px] text-ink-muted">{o.event.location}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {desktop && <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">Drag an event to move it. Click an empty spot to add one at that time.</p>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The chosen day: above the grid on the phone, beside it on desktop
// ---------------------------------------------------------------------------
function DayPanel({ day, list, people, desktop, dotsFor, onOpen, onAdd, onStep, onPick }: { day: string; list: Occurrence[]; people: Person[]; desktop: boolean; dotsFor: (d: string) => string[]; onOpen: (o: Occurrence) => void; onAdd: () => void; onStep: (n: number) => void; onPick: (d: string) => void }) {
  const d = parseISO(day); const today = ymd(new Date()); const isToday = day === today;
  const strip = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(d, { weekStartsOn: 1 }), i));
  return (
    <section className="space-y-3">
      {/* A week of days to hop between (from the ChatGPT concept). */}
      <div className="flex items-center gap-1 rounded-2xl bg-surface p-1.5 shadow-soft ring-1 ring-line/70">
        <button type="button" aria-label="Week before" onClick={() => onStep(-7)} className="flex h-11 w-8 shrink-0 items-center justify-center rounded-full text-ink-muted hover:bg-surface-raised hover:text-ink"><ChevronLeft className="h-5 w-5" /></button>
        <div className="grid min-w-0 flex-1 grid-cols-7 gap-0.5">
          {strip.map((x) => {
            const k = ymd(x); const on = k === day; const dots = dotsFor(k);
            return (
              <button key={k} type="button" onClick={() => onPick(k)} aria-pressed={on} aria-label={format(x, "EEEE d MMMM")}
                className={`flex flex-col items-center rounded-xl py-1.5 transition ${on ? "bg-primary text-primary-ink shadow-sm" : "hover:bg-primary/5"}`}>
                <span className={`text-[10px] font-medium ${on ? "opacity-90" : k === today ? "text-primary" : "text-ink-muted"}`}>{format(x, "EEE")}</span>
                <span className={`num text-[15px] font-semibold leading-tight ${!on && k === today ? "text-primary" : ""}`}>{format(x, "d")}</span>
                <span className="mt-0.5 flex h-1.5 gap-0.5">{dots.slice(0, 3).map((c, i) => <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: on ? "rgb(var(--primary-text))" : tokenColour(c) }} />)}</span>
              </button>
            );
          })}
        </div>
        <button type="button" aria-label="Week after" onClick={() => onStep(7)} className="flex h-11 w-8 shrink-0 items-center justify-center rounded-full text-ink-muted hover:bg-surface-raised hover:text-ink"><ChevronRight className="h-5 w-5" /></button>
      </div>

      <div className="overflow-hidden rounded-2xl bg-surface shadow-soft ring-1 ring-line/70">
        <div className="flex items-center gap-3 px-4 py-3" style={{ background: `radial-gradient(120% 140% at 0% 0%, rgb(var(--primary) / 0.12), transparent 60%), radial-gradient(90% 120% at 100% 0%, rgb(var(--tree-leaf) / 0.12), transparent 65%)` }}>
          <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-full bg-primary text-primary-ink shadow-sm">
            <span className="text-[10px] font-semibold uppercase tracking-wider opacity-90">{format(d, "EEE")}</span>
            <span className="num text-xl font-semibold leading-none">{format(d, "d")}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-hand text-[28px] leading-none text-ink">{isToday ? "Today" : format(d, "EEEE")}</div>
            <div className="mt-0.5 truncate text-sm text-ink-muted">{format(d, "d MMMM")}{list.length ? ` · ${list.length} on` : ""}</div>
          </div>
          <button type="button" aria-label="Day before" onClick={() => onStep(-1)} className="flex h-10 w-10 items-center justify-center rounded-full text-ink-muted hover:bg-surface-raised hover:text-ink"><ChevronLeft className="h-5 w-5" /></button>
          <button type="button" aria-label="Day after" onClick={() => onStep(1)} className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-ink-muted hover:bg-surface-raised hover:text-ink"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <div className={`space-y-2 px-3 pb-3 ${desktop ? "" : "max-h-[40vh] overflow-y-auto"}`}>
          {list.length === 0
            ? <div className="flex items-center gap-3 rounded-xl bg-bg/60 px-4 py-4 text-sm text-ink-muted"><Sun className="h-5 w-5 shrink-0 text-chart-6" />A free day. Nothing on yet.</div>
            : list.map((o) => <EventCard key={o.key} o={o} day={day} people={people} onOpen={() => onOpen(o)} />)}
          <button type="button" onClick={onAdd} className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary/10 py-3 text-sm font-semibold text-primary transition hover:bg-primary/15">
            <Plus className="h-4 w-4" /> Add to {isToday ? "today" : format(d, "EEE d")}
          </button>
        </div>
      </div>
    </section>
  );
}

/** One thing on the day, as a little card: colour edge, time, title, who, where. */
function EventCard({ o, day, people, onOpen }: { o: Occurrence; day: string; people: Person[]; onOpen: () => void }) {
  const c = colourOf(o, people);
  const { first, last } = spanOf(o);
  const when = last > first
    ? (day > first ? (day === last ? `Last day${o.end ? ` · until ${clock(o.end)}` : ""}` : `Continues · until ${format(parseISO(last), "EEE d MMM")}`) : whenText(o))
    : o.start ? `${clock(o.start)}${o.end ? ` – ${clock(o.end)}` : ""}` : "All day";
  return (
    <div className="flex overflow-hidden rounded-xl bg-surface shadow-sm ring-1 ring-line/70">
      <span className="w-1 shrink-0" style={{ background: tokenColour(c) }} />
      <div className="min-w-0 flex-1 px-3.5 py-2.5">
        <button type="button" onClick={onOpen} className="block w-full text-left">
          <span className="num block text-xs font-medium text-ink-muted">{when}</span>
          <span className="block text-[15px] font-semibold leading-snug">{occTitle(o)}</span>
        </button>
        <div className="mt-1 flex items-center gap-3 text-xs">
          <span className="flex min-w-0 items-center gap-1.5 font-medium text-ink-muted">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: tokenColour(c) }} />
            <span className="truncate">{o.event.virtual === "holiday" ? "Public holiday" : o.event.virtual === "birthday" ? "Birthday" : whoLabel(o, people)}</span>
            {o.event.repeat !== "none" && !o.event.virtual && <Repeat className="h-3 w-3 shrink-0" aria-label={o.event.repeat} />}
          </span>
          {o.event.location && (
            <a href={mapsLink(o.event.location)} target="_blank" rel="noreferrer" title={`Open ${o.event.location} in Maps`} className="ml-auto flex min-w-0 items-center gap-1 text-ink-muted hover:text-primary">
              <MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{o.event.location}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month view
// ---------------------------------------------------------------------------
function MonthView({ month, byDay, people, desktop, selected, onPick, onStepDay, onOpen, onAdd, onMove }: {
  month: Date; byDay: Map<string, Occurrence[]>; people: Person[]; desktop: boolean; selected: string; onPick: (d: string) => void; onStepDay: (n: number) => void;
  onOpen: (o: Occurrence) => void; onAdd: (date: string) => void; onMove: (o: Occurrence, date: string, start?: Date) => void;
}) {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days: Date[] = []; for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  const today = ymd(new Date());
  const drag = useRef<Occurrence | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const MAX = desktop ? 4 : 3;
  const BH = desktop ? 22 : 17;

  return (
    <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[1fr_340px] lg:items-start lg:gap-6">
      <div className="lg:sticky lg:top-6 lg:col-start-2 lg:row-start-1">
        <DayPanel day={selected} list={byDay.get(selected) ?? []} people={people} desktop={desktop} dotsFor={(k) => (byDay.get(k) ?? []).map((o) => colourOf(o, people))} onOpen={onOpen} onAdd={() => onAdd(selected)} onStep={onStepDay} onPick={onPick} />
      </div>

      <div className="overflow-hidden rounded-2xl bg-surface shadow-soft ring-1 ring-line/70 lg:col-start-1 lg:row-start-1">
        <div className="grid grid-cols-7 border-b border-line text-center text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => <div key={d} className={`py-2.5 ${i > 4 ? "text-primary/80" : ""}`}>{desktop ? d : d[0]}</div>)}
        </div>
        {Array.from({ length: days.length / 7 }, (_, w) => {
          const week = days.slice(w * 7, w * 7 + 7); const keys = week.map(ymd);
          const { placed, laneCount } = weekBars(keys, byDay, () => true);
          const shown = Math.min(MAX, laneCount);
          const hidden = keys.map((_, c) => placed.filter((p) => p.lane >= MAX && p.seg.from <= c && p.seg.to >= c).length);
          return (
            <div key={w} className={`relative grid grid-cols-7 ${w ? "border-t border-line" : ""}`} style={{ gridTemplateRows: `auto repeat(${shown}, ${BH}px) minmax(${desktop ? 22 : 16}px, 1fr)`, rowGap: desktop ? 3 : 2 }}>
              {/* Day cells: background, click, drop */}
              {week.map((d, c) => {
                const key = keys[c]; const isSel = key === selected; const inMonth = isSameMonth(d, month); const weekend = c > 4;
                return (
                  <div key={key} style={{ gridColumn: c + 1, gridRow: "1 / -1" }}
                    onDragOver={(e) => { if (drag.current) { e.preventDefault(); setOver(key); } }} onDragLeave={() => setOver(null)}
                    onDrop={(e) => { e.preventDefault(); setOver(null); const o = drag.current; drag.current = null; if (o && key !== o.date) onMove(o, key, o.start ? at(key, minsOf(o.start)) : undefined); }}
                    onClick={() => onPick(key)} onDoubleClick={() => onAdd(key)}
                    className={`min-h-[76px] cursor-pointer border-line transition-colors lg:min-h-[118px] ${c ? "border-l" : ""} ${inMonth ? (weekend ? "bg-primary/[0.025]" : "") : "bg-bg/70"} ${over === key ? "!bg-primary/10" : ""} ${isSel ? "!bg-primary/[0.08]" : "hover:bg-primary/[0.04]"}`} />
                );
              })}
              {week.map((d, c) => {
                const key = keys[c]; const isToday = key === today; const isSel = key === selected; const inMonth = isSameMonth(d, month);
                return (
                  <div key={`n${key}`} style={{ gridColumn: c + 1, gridRow: 1 }} className="pointer-events-none flex justify-center px-1.5 pb-1 pt-1.5 lg:justify-start">
                    <span className={`num flex h-7 w-7 items-center justify-center rounded-full text-sm transition ${isToday ? "bg-primary font-semibold text-white shadow-sm" : inMonth ? "" : "text-ink-muted/50"} ${isSel && !isToday ? "font-semibold text-primary ring-2 ring-primary/70" : ""}`}>{format(d, "d")}</span>
                  </div>
                );
              })}
              {placed.filter((p) => p.lane < MAX).map(({ seg, lane }) => (
                <Bar key={`${seg.o.key}:${w}`} seg={seg} people={people} desktop={desktop} onOpen={onOpen} onDragStart={() => { drag.current = seg.o; }} compact={!desktop}
                  style={{ gridColumn: `${seg.from + 1} / ${seg.to + 2}`, gridRow: lane + 2 }} />
              ))}
              {hidden.map((n, c) => n > 0 && (
                <button key={`m${c}`} type="button" onClick={() => onPick(keys[c])} style={{ gridColumn: c + 1, gridRow: shown + 2 }}
                  className="z-[1] self-start px-1.5 text-left text-[10px] font-semibold text-ink-muted hover:text-primary lg:px-2.5 lg:text-xs">+{n}{desktop ? " more" : ""}</button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Calendar() {
  const events = useEvents(); const extras = useCalendarExtras(); const people = usePeople(); const move = useMoveEvent(); const nav = useNavigate();
  const ppl = people.data ?? [];
  const desktop = useIsDesktop();
  const [view, setView] = useState<View>(() => (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches ? "week" : "month"));
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState(() => ymd(new Date()));
  const [only, setOnly] = useState<string | null>(null);
  const [sheet, setSheet] = useState<{ occ?: Occurrence; date: string; time?: string } | null>(null);
  const [pendingMove, setPendingMove] = useState<{ occ: Occurrence; date: string; start?: Date } | null>(null);
  const [peek, setPeek] = useState<PeekState>(null);
  const peekApi = useMemo(() => ({ show: (o: Occurrence, el: HTMLElement, pinned: boolean) => setPeek({ o, rect: el.getBoundingClientRect(), pinned }), hide: () => setPeek((p) => (p?.pinned ? p : null)) }), []);
  useEffect(() => { if (!peek?.pinned) return; const off = () => setPeek(null); window.addEventListener("scroll", off, true); return () => window.removeEventListener("scroll", off, true); }, [peek?.pinned]);

  const weekStart = startOfWeek(anchor, { weekStartsOn: 1 });
  const monthStart = startOfMonth(anchor);
  const range = view === "week" ? [ymd(weekStart), ymd(addDays(weekStart, 6))]
    : view === "month" ? [ymd(startOfWeek(monthStart, { weekStartsOn: 1 })), ymd(endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 }))]
    : [ymd(startOfWeek(parseISO(selected), { weekStartsOn: 1 })), ymd(addDays(parseISO(selected), 90))];

  // Looks back three weeks so a timed booking that started before the range
  // (e.g. a hotel from the 28th to the 2nd) still shows on the days it covers.
  const occ = useMemo(() => {
    const all = expand([...(events.data ?? []), ...(extras.data ?? [])], ymd(addDays(parseISO(range[0]), -21)), range[1]);
    return only ? all.filter((o) => o.people.length === 0 || o.people.includes(only)) : all;
  }, [events.data, extras.data, range[0], range[1], only]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Every day an occurrence covers, inside the range on screen. */
  const byDay = useMemo(() => {
    const m = new Map<string, Occurrence[]>();
    const put = (k: string, o: Occurrence) => { if (k >= range[0] && k <= range[1]) m.set(k, [...(m.get(k) ?? []), o]); };
    for (const o of occ) {
      if (o.event.all_day || !multiDay(o)) { put(o.date, o); continue; }
      const { last } = spanOf(o);
      for (let k = o.date; k <= last; k = ymd(addDays(parseISO(k), 1))) put(k, o);
    }
    for (const [k, list] of m) m.set(k, [...list].sort((a, b) => Number(!!a.start) - Number(!!b.start) || (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0)));
    return new Map([...m.entries()].sort(([a], [b]) => a.localeCompare(b)));
  }, [occ, range[0], range[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickDay = (d: string) => { setSelected(d); if (!isSameMonth(parseISO(d), anchor) && view !== "week") setAnchor(parseISO(d)); };
  const stepDay = (n: number) => pickDay(ymd(addDays(parseISO(selected), n)));
  // Agenda moves a week at a time; week and month move by their own size.
  const step = (n: number) => (view === "agenda" ? stepDay(7 * n) : setAnchor((a) => (view === "week" ? addWeeks(a, n) : addMonths(a, n))));
  const weekEnd = addDays(weekStart, 6);
  const [title, sub] = view === "week"
    ? [isSameMonth(weekStart, weekEnd) ? format(weekStart, "MMMM") : `${format(weekStart, "MMM")} – ${format(weekEnd, "MMM")}`, `${format(weekStart, "d MMM")} – ${format(weekEnd, "d MMM yyyy")}`]
    : view === "month" ? [format(monthStart, "MMMM"), format(monthStart, "yyyy")] : [format(parseISO(selected), "MMMM"), format(parseISO(selected), "yyyy")];
  // Coming up (agenda): each day after the chosen one; a booking shows once, on its first day.
  const upcoming = [...byDay.entries()].filter(([k]) => k > selected)
    .map(([k, list]) => [k, list.filter((o) => spanOf(o).first >= k || k === ymd(addDays(parseISO(selected), 1)))] as const)
    .filter(([, list]) => list.length > 0);
  const heroDate = view === "week" ? weekStart : view === "month" ? monthStart : parseISO(selected);

  // Holidays and birthdays are read-only: a birthday opens Contacts; a later day of a span opens the event.
  function open(o: Occurrence) {
    if (o.event.virtual === "birthday") { nav("/money/settings/contacts"); return; }
    if (o.event.virtual) return;
    setSheet({ occ: o.part ? { ...o, date: o.event.start_date ?? o.date, part: undefined } : o, date: o.event.start_date && o.part ? o.event.start_date : o.date });
  }
  function onMove(o: Occurrence, date: string, start?: Date) {
    if (o.event.virtual || o.part) return;
    if (o.date === date && (!start || !o.start || start.getTime() === o.start.getTime())) return;
    if (o.event.repeat !== "none") setPendingMove({ occ: o, date, start });
    else move.mutate({ occ: o, newDate: date, newStart: start, mode: "all" });
  }

  return (
    <PeekCtx.Provider value={peekApi}>
      <div className="mb-4 lg:mb-5">
        <CalendarHero displayDate={heroDate} title={title} sub={sub} view={view} onViewChange={setView} onNavigate={step}
          onToday={() => { setAnchor(new Date()); setSelected(ymd(new Date())); }} onCreateEvent={() => setSheet({ date: selected })} />
      </div>

      <div className="-mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-1">
        <button type="button" onClick={() => setOnly(null)} className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm transition ${only === null ? "bg-ink text-bg font-semibold" : "bg-surface text-ink-muted ring-1 ring-line hover:text-ink"}`}>Everyone</button>
        {ppl.map((p) => (
          <button key={p.id} type="button" onClick={() => setOnly(only === p.id ? null : p.id)}
            className={`flex shrink-0 items-center gap-2 rounded-full px-3.5 py-1.5 text-sm transition ${only === p.id ? "font-semibold text-white" : "bg-surface text-ink ring-1 ring-line"}`}
            style={only === p.id ? { background: tokenColour(p.colour) } : undefined}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: only === p.id ? "white" : tokenColour(p.colour) }} />{p.display_name}
          </button>
        ))}
      </div>

      {view === "week" && <WeekView weekStart={weekStart} byDay={byDay} people={ppl} desktop={desktop} onOpen={open} onAdd={(date, time) => setSheet({ date, time })} onMove={onMove} />}
      {view === "month" && <MonthView month={monthStart} byDay={byDay} people={ppl} desktop={desktop} selected={selected} onPick={pickDay} onStepDay={stepDay} onOpen={open} onAdd={(date) => setSheet({ date })} onMove={onMove} />}
      {view === "agenda" && (
        <div className="grid gap-6 lg:grid-cols-[380px_1fr] lg:items-start">
          <div className="lg:sticky lg:top-6">
            <DayPanel day={selected} list={byDay.get(selected) ?? []} people={ppl} desktop={desktop} dotsFor={(k) => (byDay.get(k) ?? []).map((o) => colourOf(o, ppl))} onOpen={open} onAdd={() => setSheet({ date: selected })} onStep={stepDay} onPick={pickDay} />
          </div>
          <div className="space-y-5">
            <h2 className="font-hand text-3xl leading-none text-primary">Coming up</h2>
            {upcoming.length === 0 && <Card className="text-ink-muted">Nothing else in the next 90 days.</Card>}
            {upcoming.map(([day, list]) => (
              <section key={day} className="space-y-2">
                <button type="button" onClick={() => pickDay(day)} className="flex items-baseline gap-2 text-left">
                  <span className="text-[15px] font-semibold">{day === ymd(addDays(new Date(), 1)) ? "Tomorrow" : format(parseISO(day), "EEEE d")}</span>
                  <span className="text-sm text-ink-muted">{format(parseISO(day), "MMMM")}</span>
                </button>
                <div className="space-y-2">{list.map((o) => <EventCard key={o.key} o={o} day={day} people={ppl} onOpen={() => open(o)} />)}</div>
              </section>
            ))}
          </div>
        </div>
      )}

      {peek && <Peek peek={peek} people={ppl} onEdit={open} onClose={() => setPeek(null)} />}
      {sheet && <EventSheet occ={sheet.occ} date={sheet.date} time={sheet.time} onClose={() => setSheet(null)} />}

      {pendingMove && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={() => setPendingMove(null)}>
          <div className="w-full max-w-sm space-y-3 rounded-lg bg-surface p-5 shadow-soft" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold">Move “{pendingMove.occ.event.title}”</h2>
            <p className="text-sm text-ink-muted">It repeats. Move just this one, or every repeat?</p>
            <Button onClick={() => { move.mutate({ occ: pendingMove.occ, newDate: pendingMove.date, newStart: pendingMove.start, mode: "one" }); setPendingMove(null); }}>Just this one</Button>
            <Button variant="ghost" onClick={() => { move.mutate({ occ: pendingMove.occ, newDate: pendingMove.date, newStart: pendingMove.start, mode: "all" }); setPendingMove(null); }}>Every repeat</Button>
          </div>
        </div>
      )}
    </PeekCtx.Provider>
  );
}
